import { config } from "@/shared/config";
import type { AuthResponse, DecodedJWT, User } from "../types";
import { AuthError } from "../types";
import { tokenStorage } from "./tokenStorage";

const REFRESH_THRESHOLD_SEC = 120; // 2 minutes before expiry

class AuthService {
  private accessToken: string | null = null;
  private user: User | null = null;
  private refreshPromise: Promise<string> | null = null;

  // ─────────────────────────────────────────────
  // Public API
  // ─────────────────────────────────────────────

  async getAccessToken(): Promise<string> {
    if (!this.accessToken) {
      return this.refreshTokens();
    }

    const tokenState = this.getTokenState(this.accessToken);

    if (tokenState === "valid") {
      return this.accessToken;
    }

    return this.refreshTokens();
  }

  async loginWithGoogle(credential: string): Promise<User> {
    const response = await fetch(
      `${config.apiBaseUrl}${config.auth.googleEndpoint}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ credential }),
      }
    );

    if (!response.ok) {
      let errorMessage = "Google authentication failed";
      try {
        const data = await response.json();
        if (data.error) errorMessage = data.error;
      } catch {}
      throw new AuthError(errorMessage, "GOOGLE_ERROR");
    }

    const data: AuthResponse = await response.json();
    this.setTokens(data);

    return this.user!;
  }

  async logout(): Promise<void> {
    const refreshToken = tokenStorage.getRefreshToken();

    if (refreshToken && this.accessToken) {
      try {
        await fetch(`${config.apiBaseUrl}${config.auth.logoutEndpoint}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${this.accessToken}`,
          },
          body: JSON.stringify({ refresh_token: refreshToken }),
        });
      } catch {
        // Ignore logout errors
      }
    }

    this.clearState();
  }

  async restoreSession(): Promise<User | null> {
    const refreshToken = tokenStorage.getRefreshToken();

    if (!refreshToken) {
      return null;
    }

    try {
      await this.refreshTokens();
      return this.user;
    } catch {
      this.clearState();
      return null;
    }
  }

  getUser(): User | null {
    return this.user;
  }

  hasStoredSession(): boolean {
    return tokenStorage.getRefreshToken() !== null;
  }

  // ─────────────────────────────────────────────
  // Token Refresh with Mutex
  // ─────────────────────────────────────────────

  private async refreshTokens(): Promise<string> {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = this.doRefresh();

    try {
      const token = await this.refreshPromise;
      return token;
    } finally {
      this.refreshPromise = null;
    }
  }

  private async doRefresh(): Promise<string> {
    const refreshToken = tokenStorage.getRefreshToken();

    if (!refreshToken) {
      throw new AuthError("No refresh token available", "INVALID_TOKEN");
    }

    const response = await fetch(
      `${config.apiBaseUrl}${config.auth.refreshEndpoint}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: refreshToken }),
      }
    );

    if (!response.ok) {
      this.clearState();
      throw new AuthError("Token refresh failed", "REFRESH_FAILED");
    }

    const data: AuthResponse = await response.json();
    this.setTokens(data);

    return this.accessToken!;
  }

  // ─────────────────────────────────────────────
  // Token State Management
  // ─────────────────────────────────────────────

  private getTokenState(token: string): "valid" | "expiring" | "expired" {
    const decoded = this.decodeJWT(token);

    if (!decoded) {
      return "expired";
    }

    const now = Math.floor(Date.now() / 1000);
    const timeUntilExpiry = decoded.exp - now;

    if (timeUntilExpiry <= 0) {
      return "expired";
    }

    if (timeUntilExpiry <= REFRESH_THRESHOLD_SEC) {
      return "expiring";
    }

    return "valid";
  }

  private decodeJWT(token: string): DecodedJWT | null {
    try {
      const parts = token.split(".");
      const payload = parts[1];
      if (!payload) return null;

      const decoded = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));

      return JSON.parse(decoded);
    } catch {
      return null;
    }
  }

  private setTokens(data: AuthResponse): void {
    this.accessToken = data.access_token;
    this.user = data.user;
    tokenStorage.setRefreshToken(data.refresh_token);
  }

  private clearState(): void {
    this.accessToken = null;
    this.user = null;
    this.refreshPromise = null;
    tokenStorage.clearRefreshToken();
  }
}

export const authService = new AuthService();
